import { useState, type SubmitEvent } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { useAuth } from '@/features/auth/hooks/useAuth'
import './SignInForm.css'

function BrandHeader() {
  return (
    <div className="sign-in__brand">
      <span className="sign-in__brand-name">DEAD CHANNEL</span>
      <span className="sign-in__brand-ice">ICE</span>
    </div>
  )
}

export function SignInForm() {
  const { signIn, confirmMfa, confirmNewPassword, challenge } = useAuth()
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  async function handleSignIn(event: SubmitEvent) {
    event.preventDefault()
    setError(null)
    setIsSubmitting(true)
    try {
      await signIn(username, password)
    } catch (error_) {
      setError(error_ instanceof Error ? error_.message : 'Sign-in failed')
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleMfa(event: SubmitEvent) {
    event.preventDefault()
    setError(null)
    setIsSubmitting(true)
    try {
      await confirmMfa(totpCode)
      setTotpCode('')
    } catch (error_) {
      setError(error_ instanceof Error ? error_.message : 'Invalid code')
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleNewPassword(event: SubmitEvent) {
    event.preventDefault()
    setError(null)
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    if (newPassword.length < 12) {
      setError('Password must be at least 12 characters')
      return
    }
    setIsSubmitting(true)
    try {
      await confirmNewPassword(newPassword)
      setNewPassword('')
      setConfirmPassword('')
    } catch (error_) {
      setError(error_ instanceof Error ? error_.message : 'Password change failed')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Forced password change (admin-created user)
  if (challenge.kind === 'new-password') {
    return (
      <div className="sign-in">
        <div className="sign-in__card">
          <BrandHeader />
          <h1 className="sign-in__title">Set a new password</h1>
          <p className="sign-in__subtitle">
            Your account requires a password change before continuing.
          </p>
          <form onSubmit={(e) => void handleNewPassword(e)}>
            <label className="sign-in__label" htmlFor="new-password">
              New password
            </label>
            <input
              id="new-password"
              className="sign-in__input"
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value)
              }}
              required
              minLength={12}
            />
            <label className="sign-in__label" htmlFor="confirm-password">
              Confirm password
            </label>
            <input
              id="confirm-password"
              className="sign-in__input"
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value)
              }}
              required
              minLength={12}
            />
            {error ? <p className="sign-in__error">{error}</p> : null}
            <button className="sign-in__button" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Set password'}
            </button>
          </form>
        </div>
      </div>
    )
  }

  // First-time TOTP setup — show QR code, then prompt for verification code
  if (challenge.kind === 'mfa-setup') {
    return (
      <div className="sign-in">
        <div className="sign-in__card sign-in__card--wide">
          <BrandHeader />
          <h1 className="sign-in__title">Set up authenticator</h1>
          <p className="sign-in__subtitle">
            Scan this QR code with your authenticator app (1Password, Authy, Google Authenticator),
            then enter the 6-digit code it generates.
          </p>

          <div className="sign-in__qr-wrap">
            <div className="sign-in__qr">
              <QRCodeSVG
                value={challenge.setupUri}
                size={180}
                bgColor="#F4F6F9"
                fgColor="#0F1720"
                level="M"
                marginSize={2}
              />
            </div>
            <div className="sign-in__qr-secret">
              <div className="sign-in__qr-secret-label">Or enter this key manually</div>
              <code className="sign-in__qr-secret-code">{challenge.sharedSecret}</code>
            </div>
          </div>

          <form onSubmit={(e) => void handleMfa(e)}>
            <label className="sign-in__label" htmlFor="totp-setup-code">
              Verification code
            </label>
            <input
              id="totp-setup-code"
              className="sign-in__input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={totpCode}
              onChange={(e) => {
                setTotpCode(e.target.value)
              }}
              placeholder="000000"
              required
              maxLength={6}
            />
            {error ? <p className="sign-in__error">{error}</p> : null}
            <button className="sign-in__button" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Verifying...' : 'Activate authenticator'}
            </button>
          </form>
        </div>
      </div>
    )
  }

  // TOTP verification (already enrolled)
  if (challenge.kind === 'mfa-code') {
    return (
      <div className="sign-in">
        <div className="sign-in__card">
          <BrandHeader />
          <h1 className="sign-in__title">TOTP verification</h1>
          <p className="sign-in__subtitle">Enter the code from your authenticator app</p>
          <form onSubmit={(e) => void handleMfa(e)}>
            <label className="sign-in__label" htmlFor="totp-code">
              Code
            </label>
            <input
              id="totp-code"
              className="sign-in__input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={totpCode}
              onChange={(e) => {
                setTotpCode(e.target.value)
              }}
              placeholder="000000"
              required
              maxLength={6}
            />
            {error ? <p className="sign-in__error">{error}</p> : null}
            <button className="sign-in__button" type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Verifying...' : 'Verify'}
            </button>
          </form>
        </div>
      </div>
    )
  }

  // Default: username + password
  return (
    <div className="sign-in">
      <div className="sign-in__card">
        <BrandHeader />
        <h1 className="sign-in__title">Sign in to The Sprawl</h1>
        <form onSubmit={(e) => void handleSignIn(e)}>
          <label className="sign-in__label" htmlFor="username">
            Username
          </label>
          <input
            id="username"
            className="sign-in__input"
            type="text"
            autoComplete="username"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value)
            }}
            required
          />
          <label className="sign-in__label" htmlFor="password">
            Password
          </label>
          <input
            id="password"
            className="sign-in__input"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value)
            }}
            required
          />
          {error ? <p className="sign-in__error">{error}</p> : null}
          <button className="sign-in__button" type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Signing in...' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  )
}
